import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BarChart3, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Anketler() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votedPolls, setVotedPolls] = useState(new Set());

  useEffect(() => {
    fetchPolls();
    const voted = JSON.parse(localStorage.getItem('votedPolls') || '[]');
    setVotedPolls(new Set(voted));
  }, []);

  const fetchPolls = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/polls`);
      const data = await response.json();
      setPolls(data);
    } catch (error) {
      toast.error('Anketler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (pollId, option) => {
    if (votedPolls.has(pollId)) {
      toast.error('Bu ankete zaten oy verdiniz');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/polls/${pollId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option })
      });

      if (response.ok) {
        const updatedPoll = await response.json();
        setPolls(polls.map(p => p.id === pollId ? updatedPoll : p));
        
        const newVotedPolls = new Set(votedPolls).add(pollId);
        setVotedPolls(newVotedPolls);
        localStorage.setItem('votedPolls', JSON.stringify([...newVotedPolls]));
        
        toast.success('Oyunuz kaydedildi!');
      } else {
        toast.error('Oy kaydedilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const calculatePercentage = (votes, option) => {
    const total = Object.values(votes).reduce((sum, val) => sum + val, 0);
    if (total === 0) return 0;
    return Math.round((votes[option] / total) * 100);
  };

  const getTotalVotes = (votes) => {
    return Object.values(votes).reduce((sum, val) => sum + val, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Anketler
          </h1>
          <p className="text-lg text-amber-700">
            Görüşlerinizi paylaşın, oy verin
          </p>
        </div>

        {polls.length === 0 ? (
          <Card className="border-2 border-amber-200 bg-white">
            <CardContent className="p-12 text-center">
              <p className="text-lg text-amber-700">Henüz aktif anket bulunmuyor</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {polls.map((poll) => {
              const hasVoted = votedPolls.has(poll.id);
              const totalVotes = getTotalVotes(poll.votes);
              
              return (
                <Card key={poll.id} className="border-2 border-amber-200 bg-white" data-testid={`poll-card-${poll.id}`}>
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-amber-900 text-xl" style={{ fontFamily: 'Merriweather, serif' }}>
                          {poll.title}
                        </CardTitle>
                        <p className="text-sm text-amber-600 mt-2">
                          Toplam oy: {totalVotes}
                        </p>
                      </div>
                      {hasVoted && (
                        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-semibold">Oy verildi</span>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {poll.options.map((option) => {
                        const percentage = calculatePercentage(poll.votes, option);
                        const voteCount = poll.votes[option] || 0;
                        
                        return (
                          <div key={option} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-amber-900 font-medium">{option}</span>
                              {hasVoted && (
                                <span className="text-sm text-amber-700">
                                  {voteCount} oy ({percentage}%)
                                </span>
                              )}
                            </div>
                            {hasVoted ? (
                              <div className="w-full bg-amber-100 rounded-full h-3 overflow-hidden">
                                <div 
                                  className="bg-gradient-to-r from-amber-600 to-orange-600 h-full rounded-full transition-all duration-500"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            ) : (
                              <Button
                                onClick={() => handleVote(poll.id, option)}
                                variant="outline"
                                className="w-full border-amber-300 text-amber-900 hover:bg-amber-50 hover:border-amber-500"
                                data-testid={`vote-option-${option}`}
                              >
                                Bu seçeneğe oy ver
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
